import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Table, Alert, Spinner, Form, InputGroup, Badge, Card } from 'react-bootstrap'
import { useAdminIssues } from 'api/hooks/use-admin-issues'

const SEVERITY_LABELS = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical', 5: 'Severe' }
const SEVERITY_COLORS = { 1: 'secondary', 2: 'info', 3: 'warning', 4: 'danger', 5: 'dark' }

const IssuesList = () => {
  const { data: issues, isLoading, error } = useAdminIssues()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredIssues = useMemo(() => {
    if (!issues) return []
    return issues.filter((issue) => {
      const term = searchTerm.toLowerCase()
      return (
        issue.name?.toLowerCase().includes(term) ||
        issue.description?.toLowerCase().includes(term) ||
        String(issue.number).includes(term) ||
        issue.datatype?.toLowerCase().includes(term)
      )
    })
  }, [issues, searchTerm])

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">Error loading issues: {error.message}</Alert>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Issues</h2>
        <Button as={Link} to="/admin/issues/new" variant="primary">
          Create Issue
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>Search</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, description, number, or datatype..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      <div className="mb-3">
        <strong>Showing {filteredIssues.length} of {issues?.length || 0} issues</strong>
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>Number</th>
            <th>Name</th>
            <th>Description</th>
            <th>Datatype</th>
            <th>Severity</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredIssues.map((issue) => (
            <tr key={issue.id}>
              <td>{issue.number}</td>
              <td>{issue.name}</td>
              <td>{issue.description?.substring(0, 80)}{issue.description?.length > 80 ? '...' : ''}</td>
              <td>{issue.datatype ? <Badge bg="info">{issue.datatype}</Badge> : <span className="text-muted">-</span>}</td>
              <td>
                <Badge bg={SEVERITY_COLORS[issue.default_severity] || 'secondary'}>
                  {SEVERITY_LABELS[issue.default_severity] || issue.default_severity}
                </Badge>
              </td>
              <td>
                <Button as={Link} to={`/admin/issues/${issue.id}`} variant="info" size="sm" className="me-2">View</Button>
                <Button as={Link} to={`/admin/issues/${issue.id}/edit`} variant="warning" size="sm">Edit</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {filteredIssues.length === 0 && <Alert variant="info">No issues found.</Alert>}
    </div>
  )
}

export default IssuesList
